import dayjs from "dayjs";
import {
  DATE_VALUE_FORMAT,
  MAX_SEGMENT_CONDITIONS,
  PREDICTED_LISTS,
  type ConditionOperator,
  type PeriodUnit,
  type SegmentConditionDraft,
} from "./CreateSegment.Constants";
import { LOCATION_OPTIONS_MOCK } from "./CreateSegment.Mocks";

// Keyword-based stand-in for the AI prompt parser until the backend endpoint exists.

export type ClarificationOptionKey = "highSpendersList" | "totalSpent";
export type UnsupportedReason = "product" | "spend";

export interface PromptToken {
  text: string;
  kind: "text" | "match" | "ambiguous" | "unsupported";
  matchNumber?: number;
}

export interface PromptClarification {
  phrase: string;
  start: number;
  end: number;
  options: Array<{ key: ClarificationOptionKey; replacement: string }>;
}

export interface PromptIssue {
  phrase: string;
  reason: UnsupportedReason;
}

export interface PromptAnalysis {
  tokens: PromptToken[];
  matchLabels: string[];
  conditions: SegmentConditionDraft[];
  operators: ConditionOperator[];
  clarifications: PromptClarification[];
  issues: PromptIssue[];
  suggestion: string;
}

type PromptResolution =
  | { kind: "match"; label: string; draft: SegmentConditionDraft }
  | { kind: "ambiguous"; options: PromptClarification["options"] }
  | { kind: "unsupported"; reason: UnsupportedReason };

type PromptMatch = PromptResolution & { start: number; end: number; text: string };

interface PromptRule {
  pattern: RegExp;
  resolve: (_match: RegExpExecArray) => PromptResolution;
}

const ORDER_SUGGESTION = "who ordered at least twice in the last 90 days.";
const PERIOD_PATTERN = "(\\d+)\\s+(days?|weeks?|months?)";
const ORDER_COUNT_WORDS: Record<string, number> = { once: 1, twice: 2 };
const OR_PATTERN = /\bor\b/i;

const escapePattern = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const alternation = (values: readonly string[]) => values.map(escapePattern).join("|");
const findCanonical = (values: readonly string[], match: string) =>
  values.find(value => value.toLowerCase() === match.toLowerCase()) ?? match;

const toPeriodUnit = (unit: string): PeriodUnit => {
  if (unit.startsWith("month")) {
    return "months";
  }
  return unit.startsWith("week") ? "weeks" : "days";
};

const toOrderCount = (value?: string) =>
  value ? ORDER_COUNT_WORDS[value.toLowerCase()] ?? Number.parseInt(value, 10) : 1;

const LIST_NAMES = PREDICTED_LISTS.map(list => list.value);
const CITIES = LOCATION_OPTIONS_MOCK.city;

const PROMPT_RULES: PromptRule[] = [
  {
    pattern: /\bbig spenders\b/gi,
    resolve: () => ({
      kind: "ambiguous",
      options: [
        { key: "highSpendersList", replacement: "High Spenders" },
        { key: "totalSpent", replacement: "total spent above an amount" },
      ],
    }),
  },
  { pattern: /\b(?:bought|purchased)\s+\w+/gi, resolve: () => ({ kind: "unsupported", reason: "product" }) },
  { pattern: /\btotal spent(?: above| over)?(?: an amount)?/gi, resolve: () => ({ kind: "unsupported", reason: "spend" }) },
  {
    pattern: new RegExp(`\\b(${alternation(LIST_NAMES)})\\b`, "gi"),
    resolve: match => {
      const listName = findCanonical(LIST_NAMES, match[1]);
      return { kind: "match", label: listName, draft: { category: "list", membership: "in", listName } };
    },
  },
  {
    pattern: new RegExp(`\\bin\\s+(${alternation(CITIES)})\\b`, "gi"),
    resolve: match => {
      const city = findCanonical(CITIES, match[1]);
      return {
        kind: "match",
        label: city,
        draft: { category: "location", field: "city", operator: "isOneOf", values: [city] },
      };
    },
  },
  {
    pattern: new RegExp(`\\bhaven'?t ordered in (?:the last )?${PERIOD_PATTERN}`, "gi"),
    resolve: match => ({
      kind: "match",
      label: match[0],
      draft: {
        category: "orderActivity",
        action: "notPlacedOrder",
        comparison: "atLeast",
        count: null,
        periodValue: Number(match[1]),
        periodUnit: toPeriodUnit(match[2]),
      },
    }),
  },
  {
    pattern: new RegExp(`\\bordered(?:\\s+at least\\s+(once|twice|\\d+\\s+times))?\\s+in the last\\s+${PERIOD_PATTERN}`, "gi"),
    resolve: match => ({
      kind: "match",
      label: match[0],
      draft: {
        category: "orderActivity",
        action: "placedOrder",
        comparison: "atLeast",
        count: toOrderCount(match[1]),
        periodValue: Number(match[2]),
        periodUnit: toPeriodUnit(match[3]),
      },
    }),
  },
  {
    pattern: /\bsigned up (before|after) (\d{4})\b/gi,
    resolve: match => {
      const isBefore = match[1].toLowerCase() === "before";
      return {
        kind: "match",
        label: match[0],
        draft: {
          category: "signupDate",
          operator: isBefore ? "before" : "after",
          date: isBefore ? `${match[2]}-01-01` : `${match[2]}-12-31`,
        },
      };
    },
  },
  {
    pattern: /\bsigned up this year\b/gi,
    resolve: match => ({
      kind: "match",
      label: match[0],
      draft: {
        category: "signupDate",
        operator: "after",
        date: dayjs().startOf("year").subtract(1, "day").format(DATE_VALUE_FORMAT),
      },
    }),
  },
];

const collectMatches = (text: string): PromptMatch[] => {
  const matches: PromptMatch[] = [];
  PROMPT_RULES.forEach(({ pattern, resolve }) => {
    pattern.lastIndex = 0;
    let match = pattern.exec(text);
    while (match) {
      matches.push({ ...resolve(match), start: match.index, end: match.index + match[0].length, text: match[0] });
      match = pattern.exec(text);
    }
  });

  matches.sort((first, second) => first.start - second.start || second.end - first.end);
  return matches.filter((match, index) => index === 0 || match.start >= matches[index - 1].end);
};

export const analyzeSegmentPromptMock = (text: string): PromptAnalysis => {
  const analysis: PromptAnalysis = {
    tokens: [],
    matchLabels: [],
    conditions: [],
    operators: [],
    clarifications: [],
    issues: [],
    suggestion: "",
  };
  let cursor = 0;
  let previousConditionEnd = -1;

  collectMatches(text).forEach(match => {
    if (match.start > cursor) {
      analysis.tokens.push({ text: text.slice(cursor, match.start), kind: "text" });
    }
    cursor = match.end;

    if (match.kind === "ambiguous") {
      analysis.tokens.push({ text: match.text, kind: "ambiguous" });
      analysis.clarifications.push({ phrase: match.text, start: match.start, end: match.end, options: match.options });
      return;
    }
    if (match.kind === "unsupported") {
      analysis.tokens.push({ text: match.text, kind: "unsupported" });
      analysis.issues.push({ phrase: match.text, reason: match.reason });
      return;
    }

    if (previousConditionEnd >= 0) {
      analysis.operators.push(OR_PATTERN.test(text.slice(previousConditionEnd, match.start)) ? "or" : "and");
    }
    previousConditionEnd = match.end;
    analysis.conditions.push(match.draft);
    analysis.matchLabels.push(match.label);
    analysis.tokens.push({ text: match.text, kind: "match", matchNumber: analysis.conditions.length });
  });

  if (cursor < text.length) {
    analysis.tokens.push({ text: text.slice(cursor), kind: "text" });
  }

  const canSuggest =
    analysis.conditions.length > 0 &&
    analysis.conditions.length < MAX_SEGMENT_CONDITIONS &&
    !analysis.clarifications.length &&
    !analysis.issues.length &&
    !analysis.conditions.some(condition => condition.category === "orderActivity") &&
    !text.trim().endsWith(".");
  if (canSuggest) {
    analysis.suggestion = `${/\s$/.test(text) ? "" : " "}${ORDER_SUGGESTION}`;
  }

  return analysis;
};
